import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import type {
  WorkOrderItem,
  WorkOrdersResponse,
  CycleCountDetails,
  BundleLocationRow,
  RecentAlertItem,
  RecentCountHistoryItem,
} from '../../types/apiTypes';
import {
  fetchWorkOrders,
  fetchWorkOrdersStream,
  fetchCycleCountDetails,
  fetchRecentAlerts,
  fetchRecentCountsBySku,
  fetchBundleLocationsByComponentSku,
  submitCycleCount,
  createManualCountRequest
} from '../../services/inventoryCountsAPI';
import { ScannerSection } from '../scanner';
import { performOCR } from '../../services/visionAPI';
import { getAllSKUs, findSKU } from '../../services/SKUListAPI';
import ProductImage from '../shared/ProductImage';
import StorageInfoModal from '../shared/StorageInfoModal';
import SkuAutocompleteInput from '../shared/SkuAutocompleteInput';
import { useSkuAutocomplete } from '../../hooks/useSkuAutocomplete';
import { useHybridShelfIndexSync } from '../../hooks/useHybridShelfIndexSync';
import { getApiErrorMessage } from '../../services/errorUtils';
import { SKU_PREFIX } from '../../constants/app.constants';
import './inventorycounts.css';
import '../labels/PrintBoxLabel.css';
import '../warehouse/SearchWarehouse.css';

interface ExpandedSections {
  manual: boolean;
  critical: boolean;
  lowStock: boolean;
  cycle: boolean;
  recentAlerts: boolean;
}

const InventoryCounts = () => {
  const [data, setData] = useState<WorkOrdersResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [selectedItem, setSelectedItem] = useState<WorkOrderItem | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState<(WorkOrderItem & CycleCountDetails & { shelfLocation?: string }) | null>(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalBundleLocations, setModalBundleLocations] = useState<BundleLocationRow[]>([]);
  const [modalBundleLocationsLoading, setModalBundleLocationsLoading] = useState(false);
  const [actualCount, setActualCount] = useState('');
  const [submitLoading, setSubmitLoading] = useState(false);
  const [recentAlerts, setRecentAlerts] = useState<RecentAlertItem[]>([]);
  const [recentAlertsLoading, setRecentAlertsLoading] = useState(false);
  const [showRecentAlertModal, setShowRecentAlertModal] = useState(false);
  const [selectedRecentAlert, setSelectedRecentAlert] = useState<RecentAlertItem | null>(null);
  const [recentAlertCounts, setRecentAlertCounts] = useState<RecentCountHistoryItem[]>([]);
  const [recentAlertModalLoading, setRecentAlertModalLoading] = useState(false);
  const [expandedSections, setExpandedSections] = useState<ExpandedSections>({
    manual: false,
    critical: false,
    lowStock: false,
    cycle: true,
    recentAlerts: false,
  });
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [scannedSku, setScannedSku] = useState('');
  const [requestMessage, setRequestMessage] = useState('');
  const [requestErrorInvalidInput, setRequestErrorInvalidInput] = useState<string | null>(null);
  const [requestSubmitting, setRequestSubmitting] = useState(false);
  const [requestShowSuccess, setRequestShowSuccess] = useState(false);
  
  const [requestManualSKU, setRequestManualSKU] = useState(SKU_PREFIX);
  const [requestAllSKUs, setRequestAllSKUs] = useState<string[]>([]);
  const [requestSkusLoading, setRequestSkusLoading] = useState(true);
  const [requestShowManualSKUInput, setRequestShowManualSKUInput] = useState(false);
  const requestSkuInputRef = useRef<HTMLInputElement>(null);
  const requestAutocompleteRef = useRef<HTMLDivElement>(null);
  const skipRequestAutocompleteOpenRef = useRef(false);
  const {
    filteredSkus: requestFilteredSKUs,
    showAutocomplete: requestShowAutocomplete,
    setShowAutocomplete: setRequestShowAutocomplete,
    selectedIndex: requestSelectedIndex,
    setSelectedIndex: setRequestSelectedIndex,
  } = useSkuAutocomplete({
    value: requestManualSKU,
    allSkus: requestAllSKUs,
    prefix: SKU_PREFIX,
    skipAutocompleteOpenRef: skipRequestAutocompleteOpenRef,
    inputRef: requestSkuInputRef,
    autocompleteRef: requestAutocompleteRef,
  });

  const toggleSection = (section: keyof ExpandedSections) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const refreshWorkOrders = useCallback(async () => {
    try {
      const result = await fetchWorkOrders();
      setData(result);
      setError(null);
    } catch (err: unknown) {
      console.error('Failed to refresh work orders:', err);
    }
  }, []);

  const refreshRecentAlerts = useCallback(async (quiet = true) => {
    if (!quiet) {
      setRecentAlertsLoading(true);
    }
    try {
      const alerts = await fetchRecentAlerts();
      setRecentAlerts(alerts);
    } catch (err: unknown) {
      console.error('Failed to refresh recent alerts:', err);
    } finally {
      if (!quiet) {
        setRecentAlertsLoading(false);
      }
    }
  }, []);

  const refreshSelectedModalDetails = useCallback(async () => {
    if (!showModal || !selectedItem?.sku) {
      return;
    }
    try {
      const [details, bundleLocations] = await Promise.all([
        fetchCycleCountDetails(selectedItem.sku),
        fetchBundleLocationsByComponentSku(selectedItem.sku),
      ]);
      setModalData((prev) => ({
        ...(prev || selectedItem),
        ...details,
      }));
      setModalBundleLocations(bundleLocations);
    } catch (err: unknown) {
      console.error('Failed to refresh cycle count details:', err);
    }
  }, [showModal, selectedItem]);

  const refreshRecentAlertModalDetails = useCallback(async (quiet = true) => {
    if (!showRecentAlertModal || !selectedRecentAlert?.sku) {
      return;
    }
    if (!quiet) {
      setRecentAlertModalLoading(true);
    }
    try {
      const counts = await fetchRecentCountsBySku(selectedRecentAlert.sku);
      setRecentAlertCounts(counts);
    } catch (err: unknown) {
      console.error('Failed to refresh recent alert count history:', err);
    } finally {
      if (!quiet) {
        setRecentAlertModalLoading(false);
      }
    }
  }, [showRecentAlertModal, selectedRecentAlert]);

  useHybridShelfIndexSync({
    enabled: true,
    onSync: async () => {
      await Promise.all([
        refreshWorkOrders(),
        refreshRecentAlerts(true),
        refreshSelectedModalDetails(),
        refreshRecentAlertModalDetails(true),
      ]);
    },
    pollMs: 20000,
    tables: ['cycle_count_events', 'inventory_count_eligibility'],
  });

  useEffect(() => {
    const loadWorkOrders = async () => {
      try {
        setLoading(true);
        setLoadingMessage('');
        setError(null);
        const result = await fetchWorkOrdersStream((message: string) => {
          setLoadingMessage(message);
        });
        setData(result);
      } catch (err: unknown) {
        setError(getApiErrorMessage(err, 'Failed to load work orders'));
      } finally {
        setLoading(false);
        setLoadingMessage('');
      }
    };

    loadWorkOrders();
  }, []);

  useEffect(() => {
    refreshRecentAlerts(false);
  }, [refreshRecentAlerts]);

  useEffect(() => {
    if (showRequestModal && requestSkusLoading) {
      const fetchSKUs = async () => {
        try {
          const skus = await getAllSKUs();
          setRequestAllSKUs(skus);
          console.log(`Loaded ${skus.length} SKUs for request modal`);
        } catch (error: unknown) {
          console.error('Error loading SKUs:', error);
        } finally {
          setRequestSkusLoading(false);
        }
      };
      fetchSKUs();
    }
  }, [showRequestModal, requestSkusLoading]);

  const requestSkuSet = useMemo(() => {
    return new Set(requestAllSKUs.map(sku => sku.toUpperCase().trim()));
  }, [requestAllSKUs]);

  const handleItemClick = async (item: WorkOrderItem) => {
    setSelectedItem(item);
    setShowModal(true);
    setModalLoading(true);
    setModalBundleLocationsLoading(true);
    setActualCount('');

    try {
      const [details, bundleLocations] = await Promise.all([
        fetchCycleCountDetails(item.sku),
        fetchBundleLocationsByComponentSku(item.sku),
      ]);
      setModalData({
        ...item,
        ...details,
      });
      setModalBundleLocations(bundleLocations);
    } catch (err: unknown) {
      setModalData({
        ...item,
        shelfLocation: 'Error loading',
        lastCountDate: null,
        countClass: 'Error',
      });
      setModalBundleLocations([]);
    } finally {
      setModalLoading(false);
      setModalBundleLocationsLoading(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setModalData(null);
    setModalBundleLocations([]);
    setModalBundleLocationsLoading(false);
    setActualCount('');
    setSelectedItem(null);
  };

  const handleRecentAlertClick = async (item: RecentAlertItem) => {
    setSelectedRecentAlert(item);
    setShowRecentAlertModal(true);
    setRecentAlertModalLoading(true);
    try {
      const counts = await fetchRecentCountsBySku(item.sku);
      setRecentAlertCounts(counts);
    } catch (err: unknown) {
      console.error('Failed to load recent alert count history:', err);
      setRecentAlertCounts([]);
    } finally {
      setRecentAlertModalLoading(false);
    }
  };

  const handleCloseRecentAlertModal = () => {
    setShowRecentAlertModal(false);
    setSelectedRecentAlert(null);
    setRecentAlertCounts([]);
    setRecentAlertModalLoading(false);
  };

  const formatMonthDay = (value: string | null | undefined): string => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return `${date.getMonth() + 1}/${date.getDate()}`;
  };

  const formatMonthDayYearShort = (value: string | null | undefined): string => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    const yy = String(date.getFullYear()).slice(-2);
    return `${mm}/${dd}/${yy}`;
  };

  const formatAlertType = (value: string): string => {
    if (value === 'LOW_STOCK_ALERT') return 'Low Stock';
    if (value === 'CRITICAL_STOCK_ALERT') return 'Critical';
    return '';
  };

  const formatShelfType = (value: string | null | undefined): string => {
    const normalized = String(value || '').trim().toLowerCase();
    if (normalized === 'pick bin') return 'Pick Bin';
    if (normalized === 'stock') return 'Stock';
    return value || '';
  };

  const getRecentAlertRowClass = (type: string): string => {
    if (type === 'CRITICAL_STOCK_ALERT') return 'ic-critical-item';
    if (type === 'LOW_STOCK_ALERT') return 'ic-low-stock-item';
    return 'ic-cycle-item';
  };

  const formatListSku = (item: WorkOrderItem): string => {
    if (!item?.sku) return '';
    return item.countComplete ? `✓ ${item.sku}` : item.sku;
  };

  const validateRequestSKU = (sku: string): boolean => {
    if (requestSkusLoading || requestAllSKUs.length === 0) {
      return false;
    }
    const normalizedSku = sku.toUpperCase().trim();
    return requestSkuSet.has(normalizedSku);
  };

  const matchRequestSKU = async (ocrText: string): Promise<string | null> => {
    const result = await findSKU(ocrText);
    if (result?.SKUFound) {
      return result.sku;
    }
    return null;
  };

  const handleRequestCapture = async (imageSrc: string | null): Promise<boolean> => {
    if (!imageSrc) return false;
    setRequestMessage('Processing image...');
    const ocrText = await performOCR(imageSrc);
    if (ocrText) {
      const detected = await matchRequestSKU(ocrText);
      if (detected) {
        if (validateRequestSKU(detected)) {
          setScannedSku(detected);
          setRequestMessage(`SKU ${detected} matched successfully.`);
          setRequestErrorInvalidInput(null);
          setTimeout(() => setRequestMessage(''), 2000);
          return true;
        }
        setRequestMessage(`SKU ${detected} Not found in Product DB`);
        setRequestErrorInvalidInput(detected);
        setTimeout(() => {
          setRequestMessage('');
          setRequestErrorInvalidInput(null);
        }, 5000);
        return false;
      }
      setRequestMessage(`Could not match SKU. Scan input: ${ocrText}`);
      setRequestErrorInvalidInput(ocrText);
      setTimeout(() => {
        setRequestMessage('');
        setRequestErrorInvalidInput(null);
      }, 5000);
      return false;
    }
    setRequestMessage('Error processing image. Please try again.');
    setRequestErrorInvalidInput(null);
    setTimeout(() => setRequestMessage(''), 3000);
    return false;
  };

  const handleRequestSKUInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value.startsWith(SKU_PREFIX)) {
      setRequestManualSKU(value);
    } else {
      setRequestManualSKU(SKU_PREFIX + value);
    }
  };

  const handleRequestSKUInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setRequestSelectedIndex(prev => 
        prev < requestFilteredSKUs.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setRequestSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
    } else if (e.key === 'Enter' && requestSelectedIndex >= 0) {
      e.preventDefault();
      selectRequestSKU(requestFilteredSKUs[requestSelectedIndex]);
    }
  };

  const selectRequestSKU = (sku: string) => {
    skipRequestAutocompleteOpenRef.current = true;
    setRequestManualSKU(sku.startsWith(SKU_PREFIX) ? sku : SKU_PREFIX + sku);
    setRequestShowAutocomplete(false);
    setRequestSelectedIndex(-1);
    if (requestSkuInputRef.current) {
      requestSkuInputRef.current.focus();
    }
  };

  const handleRequestManualSKUInput = () => {
    if (requestManualSKU.trim() && requestManualSKU.trim().length > SKU_PREFIX.length) {
      const skuValue = requestManualSKU.trim().toUpperCase();
      
      if (!validateRequestSKU(skuValue)) {
        setRequestMessage(`SKU ${skuValue} Not found in Product DB`);
        setRequestErrorInvalidInput(skuValue);
        setTimeout(() => {
          setRequestMessage('');
          setRequestErrorInvalidInput(null);
        }, 5000);
        return;
      }
      
      setScannedSku(skuValue);
      setRequestMessage(`SKU ${skuValue} set successfully.`);
      setRequestErrorInvalidInput(null);
      setRequestShowManualSKUInput(false);
      setRequestManualSKU(SKU_PREFIX);
      setTimeout(() => setRequestMessage(''), 2000);
    } else {
      setRequestMessage('Please enter a valid SKU.');
      setTimeout(() => setRequestMessage(''), 2000);
    }
  };

  const handleRequestManualSKUInputClose = () => {
    setRequestShowManualSKUInput(false);
    setRequestManualSKU(SKU_PREFIX);
  };

  const handleRequestSubmit = async () => {
    if (!scannedSku.trim() || requestSubmitting) return;
    setRequestSubmitting(true);
    setRequestMessage('');
    try {
      const newWorkOrder = await createManualCountRequest(scannedSku);
      setData(prev => ({
        ...prev!,
        manualRequests: [newWorkOrder, ...(prev?.manualRequests || [])],
      }));
      
      setRequestShowSuccess(true);
      await refreshWorkOrders();
      
      setTimeout(() => {
        setShowRequestModal(false);
        setScannedSku('');
        setRequestMessage('');
        setRequestManualSKU(SKU_PREFIX);
        setRequestShowManualSKUInput(false);
        setRequestShowSuccess(false);
      }, 1000);
    } catch (err: unknown) {
      setRequestMessage(getApiErrorMessage(err, 'Failed to create manual count'));
    } finally {
      setRequestSubmitting(false);
    }
  };

  const closeRequestModal = () => {
    setShowRequestModal(false);
    setScannedSku('');
    setRequestMessage('');
    setRequestErrorInvalidInput(null);
    setRequestManualSKU(SKU_PREFIX);
    setRequestShowManualSKUInput(false);
    setRequestSkusLoading(true);
  };


  const handleSubmitCount = async () => {
    if (!actualCount || !modalData?.id) return;

    setSubmitLoading(true);
    try {
      const submittedCount = parseInt(actualCount, 10);
      await submitCycleCount(modalData.id, submittedCount);

      setData(prevData => {
        if (!prevData) return prevData;
        const updatedManual = (prevData.manualRequests || [])
          .map(m =>
            m.id === modalData.id
              ? { ...m, countComplete: true, actualInventory: submittedCount }
              : m
          )
          .sort((a, b) => {
            if (a.countComplete === b.countComplete) return 0;
            return a.countComplete ? 1 : -1;
          });
        
        return {
          ...prevData,
          alerts: prevData.alerts.map(item =>
            item.id === modalData.id
              ? { ...item, countComplete: true, actualInventory: submittedCount }
              : item
          ),
          cycleCounts: prevData.cycleCounts.map(item =>
            item.id === modalData.id
              ? { ...item, countComplete: true, actualInventory: submittedCount }
              : item
          ),
          manualRequests: updatedManual,
        };
      });
      await refreshWorkOrders();
      await refreshSelectedModalDetails();

      handleCloseModal();
    } catch (err: unknown) {
      setError('Failed to submit count');
    } finally {
      setSubmitLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="ic-container">
        <div className="ic-loading-container">
          <div className="loading-spinner" />
          <div className="ic-loading">Loading work orders...</div>
          {loadingMessage && (
            <div className="ic-loading-message">{loadingMessage}</div>
          )}
        </div>
      </div>
    );
  }

  if (error) {
    return <div className="ic-container"><div className="ic-error">Error: {error}</div></div>;
  }

  const manualRequestsRaw = data?.manualRequests || [];
  const manualRequests = [...manualRequestsRaw].sort((a, b) => {
    if (a.countComplete === b.countComplete) {
      return 0;
    }
    return a.countComplete ? 1 : -1;
  });
  const criticalAlerts = data?.alerts?.filter(a => a.type === 'CRITICAL_STOCK_ALERT') || [];
  const lowStockAlerts = data?.alerts?.filter(a => a.type === 'LOW_STOCK_ALERT') || [];
  const cycleCounts = data?.cycleCounts || [];

  const manualAllComplete = manualRequests.length > 0 && manualRequests.every(m => m.countComplete);
  const criticalAllComplete = criticalAlerts.length > 0 && criticalAlerts.every(a => a.countComplete);
  const lowStockAllComplete = lowStockAlerts.length > 0 && lowStockAlerts.every(a => a.countComplete);
  const cycleAllComplete = cycleCounts.length > 0 && cycleCounts.every(c => c.countComplete);

  return (
    <div className="ic-container">
      {data?.summary?.cached && (
        <div className="ic-cached-badge">Using today's cached data</div>
      )}

      {criticalAlerts.length > 0 && (
        <section className="ic-section">
          <h2
            className={`ic-section-title ${criticalAllComplete ? 'ic-section-complete' : 'ic-critical'} ${expandedSections.critical ? 'ic-expanded' : ''}`}
            onClick={() => toggleSection('critical')}
          >
            <span className="ic-section-text">Critical Alerts ({criticalAlerts.length})</span>
            <span className="ic-section-arrow">{expandedSections.critical ? '▼' : '▶'}</span>
          </h2>
          {expandedSections.critical && (
            <ul className="ic-list">
              {criticalAlerts.map((item) => (
                <li
                  key={item.id || item.sku}
                  className={`ic-list-item ic-critical-item ${item.countComplete ? 'ic-complete' : ''} ${selectedItem?.sku === item.sku ? 'selected' : ''}`}
                  onClick={() => handleItemClick(item)}
                >
                  <span className="ic-sku">{formatListSku(item)}</span>
                  <span className="ic-inventory">Qty: {item.countComplete && item.actualInventory != null ? item.actualInventory : item.currentInventory}</span>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {lowStockAlerts.length > 0 && (
        <section className="ic-section">
          <h2
            className={`ic-section-title ${lowStockAllComplete ? 'ic-section-complete' : 'ic-low-stock'} ${expandedSections.lowStock ? 'ic-expanded' : ''}`}
            onClick={() => toggleSection('lowStock')}
          >
            <span className="ic-section-text">Low Stock Alerts ({lowStockAlerts.length})</span>
            <span className="ic-section-arrow">{expandedSections.lowStock ? '▼' : '▶'}</span>
          </h2>
          {expandedSections.lowStock && (
            <ul className="ic-list">
              {lowStockAlerts.map((item) => (
                <li
                  key={item.id || item.sku}
                  className={`ic-list-item ic-low-stock-item ${item.countComplete ? 'ic-complete' : ''} ${selectedItem?.sku === item.sku ? 'selected' : ''}`}
                  onClick={() => handleItemClick(item)}
                >
                  <span className="ic-sku">{formatListSku(item)}</span>
                  <span className="ic-inventory">Qty: {item.countComplete && item.actualInventory != null ? item.actualInventory : item.currentInventory}</span>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {manualRequests.length > 0 && (
        <section className="ic-section">
          <h2
            className={`ic-section-title ic-manual ${manualAllComplete ? 'ic-manual-complete' : ''} ${expandedSections.manual ? 'ic-expanded' : ''}`}
            onClick={() => toggleSection('manual')}
          >
            <span className="ic-section-text">Manual Count Requests ({manualRequests.length})</span>
            <span className="ic-section-arrow">{expandedSections.manual ? '▼' : '▶'}</span>
          </h2>
          {expandedSections.manual && (
            <ul className="ic-list">
              {manualRequests.map((item) => (
                <li
                  key={`${item.id || item.sku}-${item.countComplete ? 'complete' : 'incomplete'}`}
                  className={`ic-list-item ic-manual-item ${item.countComplete ? 'ic-complete' : ''} ${selectedItem?.sku === item.sku ? 'selected' : ''}`}
                  onClick={() => handleItemClick(item)}
                >
                  <span className="ic-sku">{formatListSku(item)}</span>
                  <span className="ic-inventory">Qty: {item.countComplete && item.actualInventory != null ? item.actualInventory : item.currentInventory}</span>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      <section className="ic-section">
        <h2
          className={`ic-section-title ${cycleAllComplete ? 'ic-section-complete' : cycleCounts.length > 0 ? 'ic-cycle' : 'ic-empty-section'} ${expandedSections.cycle ? 'ic-expanded' : ''}`}
          onClick={() => toggleSection('cycle')}
        >
          <span className="ic-section-text">Cycle Counts ({cycleCounts.length})</span>
          <span className="ic-section-arrow">{expandedSections.cycle ? '▼' : '▶'}</span>
        </h2>
        {expandedSections.cycle && cycleCounts.length > 0 && (
          <ul className="ic-list">
            {cycleCounts.map((item) => (
              <li
                key={item.id || item.sku}
                className={`ic-list-item ic-cycle-item ${item.countComplete ? 'ic-complete' : ''} ${selectedItem?.sku === item.sku ? 'selected' : ''}`}
                onClick={() => handleItemClick(item)}
              >
                <span className="ic-sku">{formatListSku(item)}</span>
                <span className="ic-inventory">Qty: {item.countComplete && item.actualInventory != null ? item.actualInventory : item.currentInventory}</span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="ic-section ic-request-section-spacing">
        <div
          className="ic-section-title ic-request-section"
          onClick={() => setShowRequestModal(true)}
        >
          <span className="ic-section-text">Request Inventory Count</span>
          <span className="ic-section-plus">+</span>
        </div>
      </section>

      <section className="ic-section">
        <h2
          className={`ic-section-title ic-cycle ${expandedSections.recentAlerts ? 'ic-expanded' : ''}`}
          onClick={() => toggleSection('recentAlerts')}
        >
          <span className="ic-section-text">See Recent Alerts ({recentAlerts.length})</span>
          <span className="ic-section-arrow">{expandedSections.recentAlerts ? '▼' : '▶'}</span>
        </h2>
        {expandedSections.recentAlerts && (
          <>
            {recentAlertsLoading ? (
              <div className="ic-loading">Loading recent alerts...</div>
            ) : recentAlerts.length === 0 ? (
              <div className="ic-empty">No recent completed alerts</div>
            ) : (
              <ul className="ic-list">
                {recentAlerts.map((item) => (
                  <li
                    key={item.id || `${item.sku}-${item.updatedAt || ''}`}
                    className={`ic-list-item ${getRecentAlertRowClass(item.type)} ${selectedRecentAlert?.id === item.id ? 'selected' : ''}`}
                    onClick={() => handleRecentAlertClick(item)}
                  >
                    <span className="ic-sku">✓ {item.sku}</span>
                    <span className="ic-inventory">{formatMonthDay(item.updatedAt || item.createdAt)}</span>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </section>

      {showRequestModal && (
        <div className="ic-modal-overlay" onClick={closeRequestModal} style={{ zIndex: requestShowManualSKUInput ? 9998 : 9999 }}>
          {requestShowSuccess && (
            <div className="success-flash" style={{ zIndex: 10001 }}>
              <div className="success-message">✓ Success!</div>
            </div>
          )}
          <div className="ic-request-modal ic-modal" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="ic-modal-close" onClick={closeRequestModal}>×</button>
            <h2 className="ic-modal-title">Request Inventory Count</h2>

            {requestMessage &&
              scannedSku &&
              !requestMessage.toLowerCase().includes('successfully') && (
                <p className="ic-request-message ic-request-error ic-request-modal-top-error" role="alert">
                  {requestMessage}
                </p>
              )}

            {scannedSku && (
              <div className="ic-modal-image-wrap">
                <ProductImage sku={scannedSku} alt={scannedSku} />
              </div>
            )}
            
            <div className="pbl-info-display">
              <div className="pbl-info-row">
                <span className="pbl-info-label">SKU:</span>
                <div className="pbl-info-value-wrapper">
                  <span 
                    className={`pbl-info-value pbl-info-clickable ${!scannedSku ? 'pbl-info-empty' : ''}`}
                    onClick={() => {
                      setRequestShowManualSKUInput(true);
                      setRequestManualSKU(scannedSku || SKU_PREFIX);
                      setTimeout(() => {
                        const modal = document.querySelector('.pbl-modal-overlay');
                        if (modal) {
                          modal.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }
                        if (requestSkuInputRef.current) {
                          requestSkuInputRef.current.focus();
                          const length = requestSkuInputRef.current.value.length;
                          requestSkuInputRef.current.setSelectionRange(length, length);
                        }
                      }, 100);
                    }}
                  >
                    {scannedSku || 'Scan or enter manually'}
                  </span>
                  {scannedSku && (
                    <button
                      className="pbl-info-clear"
                      onClick={(e) => {
                        e.stopPropagation();
                        setScannedSku('');
                        setRequestMessage('SKU cleared');
                        setTimeout(() => setRequestMessage(''), 2000);
                      }}
                      title="Clear SKU"
                    >
                      ×
                    </button>
                  )}
                </div>
              </div>
            </div>

            {!requestShowManualSKUInput && !scannedSku && (
              <div className="ic-request-webcam">
                <ScannerSection mode="barcode" onCapture={handleRequestCapture} />
              </div>
            )}

            {requestMessage && !scannedSku && (
              <p className="ic-request-message">
                {requestErrorInvalidInput && requestMessage.includes(requestErrorInvalidInput) ? (
                  <>
                    {requestMessage.split(requestErrorInvalidInput)[0]}
                    <span className="pbl-message-invalid">{requestErrorInvalidInput}</span>
                    {requestMessage.split(requestErrorInvalidInput).slice(1).join(requestErrorInvalidInput)}
                  </>
                ) : (
                  requestMessage
                )}
              </p>
            )}

            <div className="ic-request-submit-wrap">
              <button
                type="button"
                onClick={handleRequestSubmit}
                disabled={!scannedSku.trim() || requestSubmitting}
                className={`ic-submit-triangle ${scannedSku.trim() && !requestSubmitting ? 'ic-submit-active' : ''}`}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="ic-upload-icon">
                  <path d="M4 4H20V16H16M4 4V16H8" stroke="currentColor" strokeWidth="1.5" fill="none" />
                  <path d="M12 24V8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                  <path d="M9 10L12 8L15 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {requestShowManualSKUInput && (
        <div className="pbl-modal-overlay" onClick={handleRequestManualSKUInputClose} style={{ zIndex: 10000 }}>
          <div className="pbl-modal" onClick={(e) => e.stopPropagation()}>
            <button 
              className="pbl-modal-close"
              onClick={handleRequestManualSKUInputClose}
            >
              ×
            </button>
            <div className="pbl-modal-title">Enter SKU</div>
            <div className="pbl-manual-popup">
              <SkuAutocompleteInput
                inputRef={requestSkuInputRef}
                autocompleteRef={requestAutocompleteRef}
                value={requestManualSKU}
                onChange={handleRequestSKUInputChange}
                onKeyDown={handleRequestSKUInputKeyDown}
                onKeyPress={(e: React.KeyboardEvent<HTMLInputElement>) => {
                  if (e.key === 'Enter' && requestSelectedIndex < 0) {
                    handleRequestManualSKUInput();
                  }
                }}
                placeholder={`${SKU_PREFIX}...`}
                showAutocomplete={requestShowAutocomplete}
                filteredSkus={requestFilteredSKUs}
                selectedIndex={requestSelectedIndex}
                onSelectSku={selectRequestSKU}
                onHoverIndex={setRequestSelectedIndex}
              />
              <div className="webcam-capture-container" style={{ marginTop: '1rem' }}>
                <ScannerSection mode="barcode" onCapture={handleRequestCapture} />
              </div>
              <div style={{ display: 'flex', gap: '1rem', flexDirection: 'column', marginTop: '1rem' }}>
                <button
                  onClick={handleRequestManualSKUInput}
                  className="pbl-submit-button"
                  disabled={!requestManualSKU.trim() || requestManualSKU.trim().length <= SKU_PREFIX.length}
                >
                  Set SKU
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showModal && (
        <div className="ic-modal-overlay" onClick={handleCloseModal}>
          <div className="ic-modal" onClick={(e) => e.stopPropagation()}>
            <button className="ic-modal-close" onClick={handleCloseModal}>×</button>

            {modalLoading ? (
              <div className="ic-modal-loading">Loading...</div>
            ) : modalData ? (
              <>
                <h2 className="ic-modal-title">{modalData.sku}</h2>

                <div className="ic-modal-image-wrap">
                  <ProductImage sku={modalData.sku} alt={modalData.sku} />
                </div>

                {modalData.countClass === 'Error' && (
                  <div className="ic-modal-row">
                    <span className="ic-modal-value ic-modal-error">{modalData.shelfLocation}</span>
                  </div>
                )}

                <div className="ic-modal-row">
                  <span className="ic-modal-label">Expected Inventory:</span>
                  <span className="ic-modal-value">{modalData.currentInventory}</span>
                </div>

                <div className="ic-modal-row ic-modal-shelf-locations">
                  <StorageInfoModal sku={modalData.sku} title="Shelf Locations" />
                </div>

                {modalBundleLocationsLoading ? (
                  <div className="ic-modal-row ic-modal-shelf-locations">
                    <div className="sw-results-empty">Loading bundle locations...</div>
                  </div>
                ) : modalBundleLocations.length > 0 ? (
                  <div className="ic-modal-row ic-modal-shelf-locations">
                    <div className="sw-results-section">
                      <div className="sw-results-title">Bundle Locations</div>
                      <div className="sw-table-scroll">
                      <table className="sw-results-table">
                        <thead>
                          <tr>
                            <th>Bundle</th>
                            <th>Type</th>
                            <th>Shelf</th>
                          </tr>
                        </thead>
                        <tbody>
                          {modalBundleLocations.map((row, index) => (
                            <tr key={`${row.bundleSku}-${row.type}-${row.shelfIndex}-${index}`}>
                              <td>{row.bundleSku}</td>
                              <td>{formatShelfType(row.type)}</td>
                              <td>{row.shelfIndex}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      </div>
                    </div>
                  </div>
                ) : null}

                <div className="ic-modal-row">
                  <span className="ic-modal-label">Last Count:</span>
                  <span className="ic-modal-value">
                    {modalData.lastCountDate
                      ? new Date(modalData.lastCountDate).toLocaleDateString()
                      : 'Never'}
                  </span>
                </div>

                <div className="ic-modal-row">
                  <span className="ic-modal-label">Count Method:</span>
                  <span className="ic-modal-value">{modalData.countClass}</span>
                </div>

                <div className="ic-modal-input-section">
                  <label htmlFor="actualCount" className="ic-modal-label">Actual Count:</label>
                  <input
                    id="actualCount"
                    type="number"
                    className="ic-modal-input"
                    value={actualCount}
                    onChange={(e) => setActualCount(e.target.value)}
                    placeholder="Enter new count"
                    min="0"
                  />
                </div>

                <button
                  className="ic-modal-submit"
                  onClick={handleSubmitCount}
                  disabled={!actualCount || submitLoading}
                >
                  {submitLoading ? 'Submitting...' : 'Submit Count'}
                </button>
              </>
            ) : null}
          </div>
        </div>
      )}

      {showRecentAlertModal && (
        <div className="ic-modal-overlay" onClick={handleCloseRecentAlertModal}>
          <div className="ic-modal" onClick={(e) => e.stopPropagation()}>
            <button className="ic-modal-close" onClick={handleCloseRecentAlertModal}>×</button>

            {recentAlertModalLoading ? (
              <div className="ic-modal-loading">Loading...</div>
            ) : selectedRecentAlert ? (
              <>
                <h2 className="ic-modal-title">{selectedRecentAlert.sku}</h2>

                <div className="ic-modal-image-wrap">
                  <ProductImage sku={selectedRecentAlert.sku} alt={selectedRecentAlert.sku} />
                </div>

                <div className="ic-modal-row ic-modal-shelf-locations">
                  <div className="sw-results-section">
                    <div className="sw-results-title">Recent Counts</div>
                    {recentAlertCounts.length === 0 ? (
                      <div className="sw-results-empty">No recent completed counts</div>
                    ) : (
                      <div className="sw-table-scroll">
                      <table className="sw-results-table">
                        <thead>
                          <tr>
                            <th>Alert Type</th>
                            <th>Expected</th>
                            <th>Actual</th>
                            <th>Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {recentAlertCounts.map((row) => (
                            <tr key={row.id}>
                              <td>{formatAlertType(row.type)}</td>
                              <td>{row.expectedInventory ?? ''}</td>
                              <td>{row.actualInventory ?? ''}</td>
                              <td>{formatMonthDayYearShort(row.updatedAt)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      </div>
                    )}
                  </div>
                </div>
              </>
            ) : null}
          </div>
        </div>
      )}

    </div>
  );
};

export default InventoryCounts;
